import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { MenuItem, MenuList } from "@mui/material";
import cx from "classnames";
import TimeZoneSelector from "src/business-components/input/TimeZoneSelector/TimeZoneSelector";
import useBooleanState from "src/hooks/useBooleanState";
import { TIME_WINDOWS } from "@utils/constants";
import { useUserTzContext } from "../../hooks/useUserTzContext";
import { CALENDAR_PRESETS } from "../../TimeWindow.calendarPresets";
import TimeWindowCustom from "../TimeWindowCustom/TimeWindowCustom";
import TimeWindowSectionTitle from "../TimeWindowSectionTitle/TimeWindowSectionTitle";
import TimeWindowPresetOption from "./components/TimeWindowPresetOption/TimeWindowPresetOption";
import TimeWindowRangePresetOption from "./components/TimeWindowRangePresetOption/TimeWindowRangePresetOption";
import TimeWindowRecentlySelectedOptions from "./components/TimeWindowRecentlySelectedOptions/TimeWindowRecentlySelectedOptions";
import { useTimeWindowRecentHistory } from "./hooks/useTimeWindowRecentHistory";

interface TimeWindowContentProps {
  timeWindow: string;
  setTimeWindow: (value: string) => void;
  customRangeValue: [Date | null, Date | null] | undefined;
  /** Initial open state for the custom date-range panel (uncontrolled after mount). */
  defaultCustomRangeOpen?: boolean;
}

const TimeWindowContent = ({
  timeWindow,
  setTimeWindow,
  customRangeValue,
  defaultCustomRangeOpen = false,
}: TimeWindowContentProps) => {
  const tzCtx = useUserTzContext();
  const { recent, pushRecent } = useTimeWindowRecentHistory();
  const { visible: isCustomRangeOpen, toggle: toggleCustomRangeOpen } =
    useBooleanState(defaultCustomRangeOpen);

  return (
    <div>
      <div className="flex min-h-82" data-test-id="app-filters--time-window-options">
        {recent.length > 0 && (
          <aside className="order-3 border-l border-divider">
            <TimeWindowSectionTitle
              title="Recently used"
              className="pl-4 pt-2 py-1 mb-0"
            />
            <MenuList className="min-w-36">
              <TimeWindowRecentlySelectedOptions
                recent={recent}
                setTimeWindow={setTimeWindow}
                pushRecent={pushRecent}
              />
            </MenuList>
          </aside>
        )}

        <aside className="flex flex-col gap-3 px-3 py-2 order-2">
          <TimeWindowSectionTitle title="Presets" />
          <div className="flex flex-col">
            <MenuList className="min-w-36 py-0">
              {TIME_WINDOWS?.map((t) => {
                return (
                  <TimeWindowPresetOption
                    key={t}
                    t={t}
                    label={undefined}
                    tooltip={undefined}
                    timeWindow={timeWindow}
                    setTimeWindow={setTimeWindow}
                    onSelect={pushRecent}
                  />
                );
              })}
            </MenuList>
            <MenuList className="min-w-36 py-0">
              {CALENDAR_PRESETS.map(({ id, label, tooltip }) => (
                <TimeWindowRangePresetOption
                  key={id}
                  presetId={id}
                  label={label}
                  tooltip={tooltip}
                  tzCtx={tzCtx}
                  timeWindow={timeWindow}
                  setTimeWindow={setTimeWindow}
                  onSelect={pushRecent}
                />
              ))}
              <MenuItem
                data-test-id="time-window-custom-range-option"
                onClick={toggleCustomRangeOpen}
                className="justify-between! pr-8! relative gap-1 font-medium mt-3"
              >
                <CalendarMonthIcon fontSize="small" color="neutral" /> Custom range
              </MenuItem>
            </MenuList>
          </div>
        </aside>
        <aside
          className={cx(
            "order-1 overflow-hidden border-divider expandable",
            !isCustomRangeOpen ? "w-0" : "border-r"
          )}
        >
          <div className="w-[500px] h-full">
            {/* <TimeWindowSectionTitle
              title="Custom range"
              className="pl-4 pt-2 py-1 -mb-1"
            /> */}
            <TimeWindowCustom
              setSelectedValue={(value: string) => setTimeWindow(value)}
              value={customRangeValue}
              pushRecent={pushRecent}
            />
          </div>
        </aside>
      </div>
      <footer className="px-3 py-2 border-t border-divider flex">
        <TimeZoneSelector className="ml-auto" />
      </footer>
    </div>
  );
};

export default TimeWindowContent;
